// apps/merchant-v3/src/components/NotificationBell.tsx
//
// Phase 6 Wave 1 Task 02 — TopBar notification bell.
//
// Phase 6 Debug B9 — wire dropdown to real notification feed.
//   - Fetches first page via listNotifications(); cached + WS-invalidated
//     by useWsSubscribe() in NotificationsCenterPage.
//   - Renders top 8 by recency; "View all" → /notifications.
//   - Click item: mark-read + navigate to actionUrl (or /notifications).
//   - Badge surfaces unread count (red dot + N pill).
//
// vg-binding: PLAN:task-02
// vg-binding: P6.D-06
// vg-binding: DEBUG:B9
// vg-binding: CONTEXT:decisions           (P15.D-18 notification.new + version-floor, P15.D-21 signal-only WS + fail-closed + optimistic, P15.D-07 membership events; Task 25)
// vg-binding: INTERFACE-STANDARDS:error-shape

import { useCallback, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  useToast,
  type WsServerEvent,
} from '@printway/ui';
import { Bell } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/api/client';
import { useWsEvent } from '@/lib/websocket/WsProvider';
import { isNotificationBundleStale } from '@/lib/websocket/wsVersionFloor';
import { translateNotification } from '@/lib/notificationsI18n';
import {
  getUnreadCount,
  listNotifications,
  markAllRead,
  markRead,
} from '@/services/notificationsClient';
import type {
  Notification,
  NotificationsListResult,
} from '@/types/notification';

/**
 * Safety-net poll for the bell feed + unread count. WS (`notification.new`)
 * remains the fast path; this only bounds how stale the badge can get if a
 * frame is dropped or the socket is down.
 */
const BELL_POLL_MS = 30_000;

export function NotificationBell(): JSX.Element {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  // Fail-closed retry (P15.D-21): 401/403 are terminal (auth / audience) — never
  // re-hit, so a 403 AUDIENCE_FORBIDDEN surfaces the banner promptly instead of
  // burning through the default retry ladder first.
  const failClosedRetry = (failureCount: number, error: unknown): boolean => {
    if (
      error instanceof ApiError &&
      (error.status === 401 || error.status === 403)
    ) {
      return false;
    }
    return failureCount < 2;
  };

  const feedQuery = useQuery<NotificationsListResult>({
    queryKey: ['notifications', 'bell', 'all'],
    queryFn: () => listNotifications({ limit: 8 }),
    // Poll as a safety net for a dropped/absent WS frame — the same cadence
    // admin's useNotificationFeed uses. Without it the WS subscription below
    // was the ONLY refresh path, so when `notification.new` stopped being
    // published server-side the badge never moved until a manual page reload
    // (dbg-20260729-065255-9904). `staleTime` alone does not refetch.
    refetchInterval: BELL_POLL_MS,
    staleTime: 30_000,
    retry: failClosedRetry,
  });

  // dbg-20260704 (notif badge) — badge reads the merchant-wide unread total
  // from the dedicated endpoint, not the dropdown page's unread count (which
  // was capped at the 8 fetched rows and left the bell badge missing).
  const unreadQuery = useQuery<number>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => getUnreadCount(),
    refetchInterval: BELL_POLL_MS,
    staleTime: 30_000,
    retry: failClosedRetry,
  });

  const rows: Notification[] = feedQuery.data?.rows ?? [];
  // 2-case revoke (P15.D-21 / P15.D-07): the feed keys purely off the HTTP
  // status. 403 AUDIENCE_FORBIDDEN → banner + retry (NOT an empty state); 200
  // empty → real empty; 200 rows → list.
  //
  // 403-semantic (post-review FA): the BE now returns 403 AUDIENCE_FORBIDDEN
  // ONLY for a token with no userId (genuine no-identity). An ORG-revoked user
  // now gets 200 with their PERSONAL feed (possibly empty), NOT a 403 — so the
  // banner is a rare hard-error state, and a revoked member simply flows through
  // membership.revoked → refetch → 200 personal feed (handled below).
  const feedError =
    (feedQuery.error as unknown) ?? (unreadQuery.error as unknown);
  const feedForbidden =
    feedError instanceof ApiError &&
    feedError.status === 403 &&
    feedError.code === 'AUDIENCE_FORBIDDEN';
  // H5 fail-closed badge (P15.D-07 / D-21): a forbidden feed shows 0 (never a
  // stale count leaking under the banner). Otherwise trust the dedicated
  // unread-count query ONLY when it actually SUCCEEDED — a failed/pending count
  // must not fall back to a possibly-stale number; only then use the feed's
  // page-level unreadCount.
  const unreadCount = feedForbidden
    ? 0
    : unreadQuery.isSuccess
      ? (unreadQuery.data ?? 0)
      : (feedQuery.data?.unreadCount ?? 0);
  const [open, setOpen] = useState(false);
  // Mark-all-read from the bell itself (dbg-20260730-094242). Kept local — the
  // badge + feed both re-read from the invalidated queries below, so there is no
  // second source of truth to drift.
  const [markingAll, setMarkingAll] = useState(false);
  const handleMarkAllRead = useCallback(async (): Promise<void> => {
    if (markingAll) return;
    setMarkingAll(true);
    try {
      await markAllRead();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['notifications'] }),
        qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
      ]);
    } catch {
      // Surface nothing here: the badge simply stays as-is and the user can
      // retry. The full notifications page owns the toasted error path.
    } finally {
      setMarkingAll(false);
    }
  }, [markingAll, qc]);

  // #2675 B2 — the bell lives in the topbar on EVERY page, but the WS
  // invalidation only existed on NotificationsCenterPage (mounted solely at
  // /notifications). So a new ticket reply never refreshed the bell badge /
  // dropdown unless the merchant happened to be on the notifications page.
  // Subscribe here too: any notification signal refetches the bell feed +
  // unread count live (SIGNAL-ONLY — never self-increments; P15.D-21).
  const refetchBell = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
    void qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
  }, [qc]);
  // Signal-only handler (P15.D-21) that ALSO runs the version-floor check
  // (P15.D-18): an old bundle is prompted to reload; both the canonical and the
  // legacy event de-dupe by refetch, so listening to both never double-counts.
  const onNotificationSignal = useCallback(
    (ev: WsServerEvent) => {
      if (isNotificationBundleStale(ev.payload)) {
        toast({
          variant: 'default',
          title: t('topbar.notifications.reloadPrompt', {
            defaultValue:
              'A new version is available — please refresh your browser.',
          }),
        });
      }
      refetchBell();
    },
    [refetchBell, toast, t],
  );
  // Canonical signal-only (P15.D-18). The legacy `notification.create`
  // dual-listen was REMOVED in Task 29 with the server emitter (G-35).
  useWsEvent('notification.new', onNotificationSignal);
  useWsEvent('notification.read', refetchBell);
  // Membership lifecycle (P15.D-07): revoke/grant bust the badge + refetch; the
  // refetch's HTTP status decides the view (403 banner / 200 empty / rows).
  useWsEvent('membership.revoked', refetchBell);
  useWsEvent('membership.granted', refetchBell);

  // Optimistic mark-read + onError ROLLBACK + error toast (P15.D-21,
  // require-mutation-on-error ratchet). Flips the row read + decrements the badge
  // in the cache immediately; a rejected server write reverts both and surfaces
  // the failure (never a swallowed catch).
  const markReadMutation = useMutation<{ ok: true }, Error, string, {
    prevFeed: NotificationsListResult | undefined;
    prevCount: number | undefined;
  }>({
    mutationFn: (id: string) => markRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['notifications'] });
      const prevFeed = qc.getQueryData<NotificationsListResult>([
        'notifications',
        'bell',
        'all',
      ]);
      const prevCount = qc.getQueryData<number>([
        'notifications',
        'unread-count',
      ]);
      if (prevFeed) {
        qc.setQueryData<NotificationsListResult>(
          ['notifications', 'bell', 'all'],
          {
            ...prevFeed,
            rows: prevFeed.rows.map((r) =>
              r.id === id && !r.isRead ? { ...r, isRead: true } : r,
            ),
          },
        );
      }
      if (typeof prevCount === 'number') {
        qc.setQueryData<number>(
          ['notifications', 'unread-count'],
          Math.max(0, prevCount - 1),
        );
      }
      return { prevFeed, prevCount };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.prevFeed !== undefined) {
        qc.setQueryData(['notifications', 'bell', 'all'], ctx.prevFeed);
      }
      if (ctx?.prevCount !== undefined) {
        qc.setQueryData(['notifications', 'unread-count'], ctx.prevCount);
      }
      toast({
        variant: 'error',
        title: t('topbar.notifications.markReadError', {
          defaultValue: "Couldn't mark as read. Please try again.",
        }),
        description: err instanceof Error ? err.message : undefined,
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Retry after a 403 AUDIENCE_FORBIDDEN (P15.D-21) — re-runs both queries; the
  // fresh HTTP status flips the view between banner / empty / list.
  const retryFeed = useCallback(() => {
    void feedQuery.refetch();
    void unreadQuery.refetch();
  }, [feedQuery, unreadQuery]);

  function handleItemClick(n: Notification): void {
    setOpen(false);
    if (!n.isRead) {
      markReadMutation.mutate(n.id);
    }
    // When the row carries a deep-link, jump straight to the entity
    // surface. Otherwise route to the dedicated detail page so the
    // merchant can read the full body + decide an action — falling
    // back to /notifications (list) left the click looking dead.
    const target = n.actionUrl ?? `/notifications/${encodeURIComponent(n.id)}`;
    navigate(target);
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="topbar-notification-bell"
          aria-label={
            unreadCount > 0
              ? t('topbar.notifications.ariaWithCount', {
                  count: unreadCount,
                  defaultValue: 'Notifications ({{count}} unread)',
                })
              : t('topbar.notifications.title', {
                  defaultValue: 'Notifications',
                })
          }
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-pw-text-muted hover:bg-pw-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <Bell className="h-5 w-5" aria-hidden />
          {unreadCount > 0 ? (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="z-50 w-[360px] rounded-md border border-pw-border bg-pw-card p-0 shadow-lg"
        data-testid="topbar-notification-dropdown"
      >
          <div className="flex items-center justify-between border-b border-pw-border px-3 py-2">
            <span className="text-sm font-semibold text-pw-text">
              {t('topbar.notifications.title', { defaultValue: 'Notifications' })}
            </span>
            {unreadCount > 0 ? (
              <span className="text-xs font-medium text-pw-text-muted">
                {t('topbar.notifications.unreadCount', {
                  count: unreadCount,
                  defaultValue: '{{count}} unread',
                })}
              </span>
            ) : null}
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {feedForbidden ? (
              // 2-case revoke (P15.D-21): 403 AUDIENCE_FORBIDDEN → banner +
              // retry, NOT a silent empty state.
              <div
                role="alert"
                data-testid="notif-forbidden-banner"
                className="flex flex-col items-center gap-2 px-4 py-8 text-center"
              >
                <p className="text-sm font-medium text-pw-text">
                  {t('topbar.notifications.forbidden.title', {
                    defaultValue: 'Notifications unavailable',
                  })}
                </p>
                <p className="text-xs text-pw-text-muted">
                  {t('topbar.notifications.forbidden.body', {
                    defaultValue:
                      "You don't have access to a notification feed right now.",
                  })}
                </p>
                <button
                  type="button"
                  data-testid="notif-retry-btn"
                  onClick={retryFeed}
                  disabled={feedQuery.isFetching || unreadQuery.isFetching}
                  className="mt-1 rounded-md border border-pw-border px-3 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-pw-surface disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('topbar.notifications.forbidden.retry', {
                    defaultValue: 'Retry',
                  })}
                </button>
              </div>
            ) : feedQuery.isLoading ? (
              <div className="px-4 py-8 text-center text-sm text-pw-text-muted">
                {t('topbar.notifications.loading', { defaultValue: 'Loading…' })}
              </div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-pw-text-muted">
                {t('topbar.notifications.empty', {
                  defaultValue: 'No notifications yet.',
                })}
              </div>
            ) : (
              <ul className="divide-y divide-pw-border">
                {rows.map((n) => {
                  // Re-render at the active locale when the row carries an
                  // i18n key (dbg-20260704-110206-3372).
                  const tr = translateNotification(n, i18n.language);
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        data-testid={`topbar-notification-item-${n.id}`}
                        onClick={() => handleItemClick(n)}
                        className={cn(
                          'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-pw-surface',
                          !n.isRead && 'bg-brand-50/40',
                        )}
                      >
                        {!n.isRead ? (
                          <span
                            aria-hidden
                            className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500"
                          />
                        ) : (
                          <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-pw-text">
                            {tr.title}
                          </div>
                          {tr.body ? (
                            <div className="line-clamp-2 text-xs text-pw-text-muted">
                              {tr.body}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer: mark-all-read + view-all. The bell used to offer ONLY
              "View all", so clearing the badge meant navigating to the full
              notifications page first. dbg-20260730-094242. */}
          <div className="flex items-center justify-between gap-2 border-t border-pw-border px-3 py-2">
            <button
              type="button"
              disabled={unreadCount === 0 || markingAll}
              onClick={(e) => {
                // Keep the dropdown open so the badge visibly clears in place.
                e.preventDefault();
                void handleMarkAllRead();
              }}
              className="text-xs font-medium text-pw-text-muted hover:text-pw-text disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="notification-bell-mark-all-read"
            >
              {t('topbar.notifications.markAllRead', {
                defaultValue: 'Mark all as read',
              })}
            </button>
            <DropdownMenuItem asChild>
              <Link
                to="/notifications"
                className="text-xs font-medium text-brand-600 hover:underline"
              >
                {t('topbar.notifications.viewAll', { defaultValue: 'View all' })}
              </Link>
            </DropdownMenuItem>
          </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationBell;
